import React, { useEffect, useState, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const toAbs = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);

/**
 * Full-screen image lightbox with left/right navigation, keyboard shortcuts
 * (Esc = close, Left/Right = prev/next, +/- = zoom, 0 = reset), and download.
 *
 * Props:
 *  - images: array of {url, name, uploaded_by_name?, uploaded_at?}
 *  - index: number — currently-visible image index
 *  - onClose: () => void
 *  - onIndexChange?: (newIndex) => void
 */
const ImageLightbox = ({ images = [], index = 0, onClose, onIndexChange }) => {
  const [current, setCurrent] = useState(index);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panningRef = useRef(null);

  useEffect(() => setCurrent(index), [index]);
  useEffect(() => { setScale(1); setPan({ x: 0, y: 0 }); }, [current]);

  const go = useCallback((delta) => {
    if (!images.length) return;
    const next = (current + delta + images.length) % images.length;
    setCurrent(next);
    onIndexChange?.(next);
  }, [current, images.length, onIndexChange]);

  const zoomIn = () => setScale((s) => Math.min(s * 1.25, 4));
  const zoomOut = () => setScale((s) => Math.max(s / 1.25, 1));
  const resetZoom = () => { setScale(1); setPan({ x: 0, y: 0 }); };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(+1);
      else if (e.key === '+' || e.key === '=') zoomIn();
      else if (e.key === '-' || e.key === '_') zoomOut();
      else if (e.key === '0') resetZoom();
    };
    window.addEventListener('keydown', onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [go, onClose]);

  if (!images.length) return null;
  const img = images[current] || images[0];
  const href = toAbs(img?.url);
  const total = images.length;

  const onImgMouseDown = (e) => {
    if (scale <= 1) return;
    panningRef.current = { startX: e.clientX - pan.x, startY: e.clientY - pan.y };
  };
  const onImgMouseMove = (e) => {
    if (!panningRef.current) return;
    setPan({ x: e.clientX - panningRef.current.startX, y: e.clientY - panningRef.current.startY });
  };
  const onImgMouseUp = () => { panningRef.current = null; };

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      style={{ background: 'rgba(8, 12, 20, 0.95)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="image-lightbox"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 md:px-6 py-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm md:text-base font-semibold truncate" title={img?.name}>{img?.name || 'Imagine'}</p>
          <p className="text-[11px] text-slate-300 truncate">
            {current + 1} / {total}
            {img?.uploaded_by_name ? ` · ${img.uploaded_by_name}` : ''}
            {img?.uploaded_at ? ` · ${new Date(img.uploaded_at).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <button
            type="button"
            onClick={zoomOut}
            className="p-2 rounded-lg hover:bg-white/10 text-white disabled:opacity-30"
            disabled={scale <= 1}
            title="Zoom out ( - )"
            data-testid="lightbox-zoom-out"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="text-xs text-slate-300 w-12 text-center tabular-nums">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            onClick={zoomIn}
            className="p-2 rounded-lg hover:bg-white/10 text-white disabled:opacity-30"
            disabled={scale >= 4}
            title="Zoom in ( + )"
            data-testid="lightbox-zoom-in"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={resetZoom}
            className="p-2 rounded-lg hover:bg-white/10 text-white"
            title="Reset zoom ( 0 )"
            data-testid="lightbox-zoom-reset"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          <a
            href={href}
            download={img?.name}
            onClick={(e) => e.stopPropagation()}
            className="p-2 rounded-lg hover:bg-white/10 text-white hidden md:inline-flex"
            title="Descarca"
            data-testid="lightbox-download"
          >
            <Download className="w-5 h-5" />
          </a>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-white"
            title="Inchide (Esc)"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div
        className="flex-1 relative flex items-center justify-center overflow-hidden select-none"
        onClick={(e) => e.stopPropagation()}
        onMouseMove={onImgMouseMove}
        onMouseUp={onImgMouseUp}
        onMouseLeave={onImgMouseUp}
      >
        {total > 1 && (
          <button
            type="button"
            onClick={() => go(-1)}
            className="absolute left-2 md:left-6 z-10 p-3 rounded-full bg-black/40 hover:bg-black/70 text-white transition-colors"
            title="Anterioara (<-)"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6 md:w-7 md:h-7" />
          </button>
        )}

        <img
          key={img?.url}
          src={href}
          alt={img?.name || ''}
          onMouseDown={onImgMouseDown}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            cursor: scale > 1 ? (panningRef.current ? 'grabbing' : 'grab') : 'default',
            transition: panningRef.current ? 'none' : 'transform 0.15s ease-out',
            maxWidth: '92vw',
            maxHeight: '78vh',
          }}
          className="object-contain shadow-2xl rounded-sm pointer-events-auto"
          draggable={false}
        />

        {total > 1 && (
          <button
            type="button"
            onClick={() => go(+1)}
            className="absolute right-2 md:right-6 z-10 p-3 rounded-full bg-black/40 hover:bg-black/70 text-white transition-colors"
            title="Urmatoarea (->)"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6 md:w-7 md:h-7" />
          </button>
        )}
      </div>

      {/* Thumbnails row (hidden on <md if many) */}
      {total > 1 && (
        <div
          className="px-4 py-3 flex gap-2 overflow-x-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((im, i) => (
            <button
              key={im.url + i}
              type="button"
              onClick={() => { setCurrent(i); onIndexChange?.(i); }}
              className={`flex-shrink-0 rounded border-2 ${i === current ? 'border-indigo-400' : 'border-transparent opacity-60 hover:opacity-100'}`}
              data-testid={`lightbox-thumb-${i}`}
            >
              <img
                src={toAbs(im.url)}
                alt=""
                className="w-14 h-14 md:w-16 md:h-16 object-cover rounded"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;
